import { StandardEvents } from '@shopify/events';
import { formatMoney } from '@theme/money-formatting';
import { parsePlanTiers, pickPlan, tierForQuantity } from '@theme/selling-plans';

/**
 * Subscribe & save / one-time purchase card with pack-size tier pricing.
 *
 * Reads the tier published by pdp-pack-size.js (`data-pack-count` /
 * `data-pack-percent` on the section root, `pdp:pack-change` events) and the
 * bundle builder's lines (`flavor-bundle:change`), prices both rows from them,
 * and keeps the hidden `selling_plan` input in sync with the selected row.
 * The add-to-cart button carries no price: the selected row already shows
 * the total the shopper will pay.
 *
 * Selling plans are tiered by item count (`data-plan-tiers`, see
 * snippets/selling-plan-tiers): the cadence picker holds the delivery
 * frequency, the pack count picks the tier, and the hidden input carries the
 * plan for that pair — so a single pack can never submit the two-pack price.
 *
 * On variant change it re-renders itself from the fetched section HTML
 * (same source product-price morphs from), keeping the shopper's choices.
 *
 * The purchase mode and cadence are the shopper's choice, kept per product
 * in sessionStorage and put back on connect and on every `pageshow` (Back
 * from the checkout, a bfcache restore); the controls carry
 * `autocomplete="off"` so the browser's own form restoration never puts a
 * stale choice over the current pack tier.
 *
 * All lookups are live queries and the change listener is delegated on the
 * host element, so replacing the children after a variant morph needs no
 * rebinding.
 */
const STORAGE_PREFIX = 'tiiga-pdp-buy:';

class PdpBuyBoxComponent extends HTMLElement {
  /** @type {AbortController} */
  #abortController = new AbortController();

  /** Bundle builder state, or null while in one-flavor mode */
  /** @type {{ count: number, prices: number[], valid: boolean } | null} */
  #bundle = null;

  connectedCallback() {
    // A fresh controller per connection: a node a morph moves is
    // disconnected and connected again, and an aborted signal adds nothing
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    const section = this.#section;

    this.addEventListener('change', this.#apply, { signal });
    section?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    section?.addEventListener('pdp:pack-change', this.#handlePackChange, { signal });
    section?.addEventListener('flavor-bundle:change', this.#handleBundleChange, { signal });

    // The pack count may already differ from the server default (a restored
    // tier, a pack-type switch): offer that tier's cadences from the start
    this.#restore();
    window.addEventListener('pageshow', this.#restore, { signal });

    // Recharge's (CSS-hidden) widget injects its own selling_plan field into
    // the product form, defaulting to its first plan ("1 product every 15
    // days"); the last field wins, so the cart would get that plan instead of
    // the one picked here. Silence every plan field that isn't ours, both now
    // and at the last moment before Horizon reads the form. The widget loads
    // after this connects, and Horizon reads the form from a capturing
    // listener on `document` registered at page load, so this one listens on
    // `window`, the first stop of the capture path.
    window.addEventListener('submit', this.#muteForeignPlanInputs, { capture: true, signal });
    // Every read of the form — a direct add, or an add queued during a
    // variant change, after which the widget may have re-rendered its field —
    // gets this plan, whatever else the form holds
    window.addEventListener('formdata', this.#claimPlanEntry, { capture: true, signal });
  }

  disconnectedCallback() {
    this.#abortController.abort();
  }

  get #section() {
    return this.closest('.product-information') ?? this.closest('.shopify-section');
  }

  get #modeInputs() {
    return /** @type {HTMLInputElement[]} */ ([...this.querySelectorAll('input[type="radio"]')]);
  }

  get #planSelect() {
    return /** @type {HTMLSelectElement | null} */ (this.querySelector('.pdp-buy__plan-select'));
  }

  /** The cadence options of the tiers not currently on offer */
  get #planPool() {
    return /** @type {HTMLTemplateElement | null} */ (this.querySelector('template.pdp-buy__plan-pool'));
  }

  /**
   * Puts the current tier's cadences in the select and parks the others:
   * each item count has its own plans, and their cadences differ ("3 tubs"
   * ships every 60 to 120 days, "1 tub" every 30 to 60).
   * @returns {boolean} Whether the options on offer changed
   */
  #syncPlanOptions() {
    const planSelect = this.#planSelect;
    const pool = this.#planPool;
    if (!planSelect || !pool) return false;

    const tier = tierForQuantity(this.#planEntries, this.#packCount);
    const options = [...planSelect.options, ...pool.content.querySelectorAll('option')];
    const byOrd = (/** @type {HTMLOptionElement} */ a, /** @type {HTMLOptionElement} */ b) =>
      Number(a.dataset.ord) - Number(b.dataset.ord);
    const offered = options.filter((option) => Number(option.dataset.tier) === tier).sort(byOrd);
    const parked = options.filter((option) => Number(option.dataset.tier) !== tier);
    if (offered.length === 0) return false;

    const unchanged =
      offered.length === planSelect.options.length && offered.every((option, index) => planSelect.options[index] === option);
    if (unchanged) return false;

    const previousOrd = this.#selectedOrd;
    planSelect.replaceChildren(...offered);
    pool.content.replaceChildren(...parked);
    const kept = offered.find((option) => Number(option.dataset.ord) === previousOrd);
    if (kept) planSelect.value = kept.value;
    return true;
  }

  /**
   * Picks the cadence that lasts about as long as the pack: servings ÷
   * servings-per-week, nearest option by its cadence in days (16 servings
   * at 8/week → 14 days, "2 weeks").
   */
  #pickNearestCadence() {
    const planSelect = this.#planSelect;
    const servings = Number(this.dataset.servings) || 0;
    const perWeek = Number(this.dataset.servingsPerWeek) || 8;
    if (!planSelect || servings <= 0) return;

    const targetDays = Math.round((servings * this.#packCount * 7) / perWeek);
    let best = null;
    let bestGap = Infinity;
    for (const option of planSelect.options) {
      const ord = Number(option.dataset.ord ?? option.value);
      if (!Number.isFinite(ord) || ord <= 0) continue;
      const gap = Math.abs(ord - targetDays);
      if (gap < bestGap) {
        bestGap = gap;
        best = option;
      }
    }
    if (best) planSelect.value = best.value;
  }

  /**
   * Re-derives the plan options, cadence and prices from the controls as
   * they stand: on connect, and again once the browser has restored a page
   * from history (its form restoration can land after connect).
   */
  #restore = () => {
    const stored = this.#stored;
    if (stored?.mode) {
      for (const input of this.#modeInputs) input.checked = input.value === stored.mode;
    }
    const changed = this.#syncPlanOptions();
    const planSelect = this.#planSelect;
    const kept = stored?.ord
      ? [...(planSelect?.options ?? [])].find((option) => Number(option.dataset.ord) === stored.ord)
      : undefined;
    if (kept && planSelect) planSelect.value = kept.value;
    else if (changed || !this.#selectedOrd) this.#pickNearestCadence();
    this.#lastPackCount = this.#packCount;
    this.#apply();
  };

  get #storageKey() {
    return `${STORAGE_PREFIX}${this.dataset.productId ?? this.dataset.blockId ?? ''}`;
  }

  /** The mode and cadence kept for this product in this session */
  get #stored() {
    try {
      const raw = sessionStorage.getItem(this.#storageKey);
      const parsed = raw ? JSON.parse(raw) : null;
      if (!parsed || typeof parsed !== 'object') return null;
      return {
        mode: parsed.mode === 'subscribe' || parsed.mode === 'one-time' ? parsed.mode : '',
        ord: Number(parsed.ord) || 0,
      };
    } catch {
      return null;
    }
  }

  #store() {
    try {
      sessionStorage.setItem(this.#storageKey, JSON.stringify({ mode: this.#selectedMode, ord: this.#selectedOrd }));
    } catch {
      // Storage blocked: the page still works, Back just won't remember
    }
  }

  get #selectedMode() {
    return this.#modeInputs.find((input) => input.checked)?.value ?? 'one-time';
  }

  get #packCount() {
    return Math.max(1, Number(this.#section?.dataset.packCount) || 1);
  }

  get #packPercent() {
    return Math.max(0, Number(this.#section?.dataset.packPercent) || 0);
  }

  /** @type {{ raw: string, entries: import('@theme/selling-plans').PlanTierEntry[] } | null} */
  #planCache = null;

  get #planEntries() {
    const raw = this.dataset.planTiers ?? '';
    if (this.#planCache?.raw !== raw) this.#planCache = { raw, entries: parsePlanTiers(raw) };
    return this.#planCache.entries;
  }

  /** The cadence picked, in days. */
  get #selectedOrd() {
    return Number(this.#planSelect?.selectedOptions[0]?.dataset.ord ?? this.#planSelect?.value) || 0;
  }

  /**
   * The plan for the current pack count at the picked cadence. Falls back
   * to the cadence's first-tier plan when the tier lacks that cadence.
   */
  get #resolvedPlan() {
    const entries = this.#planEntries;
    const ord = this.#selectedOrd;
    if (entries.length === 0 || !ord) return null;
    return pickPlan(entries, this.#packCount, ord) ?? entries.find((entry) => entry.ord === ord) ?? null;
  }

  /**
   * The saving shown for the current tier: the pack selector's (the block
   * setting — the first-order saving, plan discount plus the automatic
   * first-order discount), or the resolved plan's own discount when the
   * setting has none for this count.
   */
  get #subscribePercent() {
    const packPercent = this.#packPercent;
    if (packPercent > 0) return packPercent;
    return this.#resolvedPlan?.percent ?? 0;
  }

  /** @param {Event} event */
  #handleBundleChange = (event) => {
    const detail = /** @type {CustomEvent} */ (event).detail ?? {};
    this.#bundle =
      detail.mode === 'bundle'
        ? {
            count: Number(detail.count) || 0,
            prices: Array.isArray(detail.prices) ? detail.prices : [],
            valid: Boolean(detail.valid),
          }
        : null;
    this.#apply();
  };

  /** The pack count the cadence was last picked for */
  #lastPackCount = 0;

  /**
   * A new pack size swaps in that tier's cadences and resets the delivery
   * frequency to the one that lasts about as long as the pack. The selector
   * re-publishes its count after every variant change, so a repeat of the
   * same count keeps the cadence the shopper picked.
   */
  #handlePackChange = () => {
    const count = this.#packCount;
    const changed = this.#syncPlanOptions();
    if (changed || count !== this.#lastPackCount) this.#pickNearestCadence();
    this.#lastPackCount = count;
    this.#apply();
  };

  get #sellingPlanInput() {
    return /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="selling_plan"]'));
  }

  #muteForeignPlanInputs = () => {
    const form = this.#sellingPlanInput?.form;
    if (!form) return;
    // Recharge renders a <select name="selling_plan"> plus radio inputs
    for (const field of form.querySelectorAll('[name="selling_plan"], [name^="selling_plan_"]')) {
      if (
        (field instanceof HTMLInputElement || field instanceof HTMLSelectElement) &&
        field !== this.#sellingPlanInput
      ) {
        field.disabled = true;
      }
    }
  };

  /** @param {Event} event */
  #claimPlanEntry = (event) => {
    const input = this.#sellingPlanInput;
    if (!(event instanceof FormDataEvent) || !input?.form || event.target !== input.form) return;
    if (input.value) {
      event.formData.set('selling_plan', input.value);
    } else {
      event.formData.delete('selling_plan');
    }
  };

  #apply = () => {
    const subscribe = this.#selectedMode === 'subscribe';
    const planSelect = this.#planSelect;
    // A select left with no selection (a restored page, a swapped tier)
    // would submit a subscription without a plan: pick a cadence first
    if (planSelect && planSelect.options.length > 0 && !this.#selectedOrd) this.#pickNearestCadence();

    const sellingPlanInput = this.#sellingPlanInput;
    if (sellingPlanInput) {
      const plan = subscribe && planSelect ? this.#resolvedPlan : null;
      sellingPlanInput.value = plan ? String(plan.id) : '';
    }
    this.#muteForeignPlanInputs();

    for (const input of this.#modeInputs) {
      input.closest('.pdp-buy__option')?.classList.toggle('pdp-buy__option--selected', input.checked);
    }

    this.#store();
    this.#renderPrices(subscribe);
  };

  /** @param {boolean} subscribe */
  #renderPrices(subscribe) {
    const unitPrice = Number(this.dataset.price) || 0;
    const servings = Number(this.dataset.servings) || 0;
    const percent = this.#subscribePercent;

    // The total always prices a full pack. In bundle mode the filled slots
    // bring their own prices (a bundle can mix sibling products — the variety
    // packs), and the slots still empty are priced at this variant's rate.
    const count = this.#packCount;
    const bundleLines = this.#bundle ? this.#bundle.prices.slice(0, count) : [];
    const listTotal =
      bundleLines.reduce((sum, price) => sum + price, 0) + unitPrice * Math.max(0, count - bundleLines.length);
    // Subscribe & save lands on a whole dollar: a $26.99 result shows as $27
    const subscribeTotal = Math.round((listTotal * (1 - percent / 100)) / 100) * 100;
    const totalServings = servings * count;

    this.#setText('subscribeNow', this.#money(subscribeTotal));
    this.#setText('oneTimeNow', this.#money(listTotal));

    const was = this.#ref('subscribeWas');
    if (was) {
      was.textContent = this.#money(listTotal);
      was.hidden = subscribeTotal >= listTotal;
    }

    // Orders after the first are charged the plan's own price, not the
    // first-order total above: the plan's per-unit price over the variant's,
    // applied to the pack (exact for one flavor, the plan's rate for a mix)
    const renewal = this.#ref('renewal');
    if (renewal) {
      const plan = this.#resolvedPlan;
      const cadence = this.#planSelect?.selectedOptions[0]?.textContent?.trim() ?? '';
      const renewalTotal = plan && plan.price > 0 && unitPrice > 0 ? Math.round((listTotal * plan.price) / unitPrice) : 0;
      renewal.hidden = renewalTotal <= 0 || !cadence;
      if (!renewal.hidden) {
        renewal.textContent = (this.dataset.renewalLabel ?? '')
          .replace('[price]', this.#money(renewalTotal))
          .replace('[cadence]', cadence);
      }
    }

    const badge = this.#ref('badge');
    if (badge) {
      badge.textContent = (this.dataset.saveLabel ?? '').replace('[percent]', String(percent));
      badge.hidden = percent <= 0;
    }

    if (totalServings > 0) {
      const label = this.dataset.perServingLabel ?? '[price]';
      this.#setText('subscribeUnit', label.replace('[price]', this.#money(Math.round(subscribeTotal / totalServings))));
      this.#setText('oneTimeUnit', label.replace('[price]', this.#money(Math.round(listTotal / totalServings))));
    }
  }

  /**
   * @param {string} name
   * @returns {HTMLElement | null}
   */
  #ref(name) {
    const element = this.querySelector(`[data-ref="${name}"]`);
    return element instanceof HTMLElement ? element : null;
  }

  /**
   * @param {string} name
   * @param {string} text
   */
  #setText(name, text) {
    const element = this.#ref(name);
    if (element) element.textContent = text;
  }

  /** @param {number} cents */
  #money(cents) {
    return formatMoney(cents, this.dataset.moneyFormat || '{{amount}}', this.dataset.currency || 'USD');
  }

  /**
   * Re-render from the updated section HTML after a variant change, keeping
   * the shopper's subscribe / one-time choice and delivery frequency.
   * @param {CustomEvent & { promise?: Promise<{ detail?: { html?: Document, productId?: string } }> }} event
   */
  #handleProductSelect = (event) => {
    event.promise?.then(({ detail }) => {
      const html = detail?.html;
      if (!html) return;
      if (detail?.productId && detail.productId !== this.dataset.productId) return;

      const replacement = html.querySelector(`pdp-buy-box-component[data-block-id="${this.dataset.blockId}"]`);
      if (!(replacement instanceof HTMLElement)) return;

      const previousMode = this.#selectedMode;
      const previousPlan = this.#planSelect?.value;

      this.dataset.price = replacement.dataset.price ?? this.dataset.price;
      this.dataset.servings = replacement.dataset.servings ?? this.dataset.servings;
      this.dataset.planTiers = replacement.dataset.planTiers ?? '';
      this.replaceChildren(.../** @type {Element} */ (replacement.cloneNode(true)).childNodes);

      for (const input of this.#modeInputs) {
        input.checked = input.value === previousMode;
      }
      this.#syncPlanOptions();
      const planSelect = this.#planSelect;
      if (planSelect && previousPlan && [...planSelect.options].some((option) => option.value === previousPlan)) {
        planSelect.value = previousPlan;
      } else {
        this.#pickNearestCadence();
      }

      this.#apply();
    });
  };
}

if (!customElements.get('pdp-buy-box-component')) {
  customElements.define('pdp-buy-box-component', PdpBuyBoxComponent);
}
